import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  PROPERTY_SYSTEM_TYPES,
  PROPERTY_SYSTEM_TYPE_LABELS,
} from "@mavatech/shared";
import { Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { PropertyJournalSection } from "@/pages/Properties/PropertyJournalSection";

const SYSTEM_TYPES = PROPERTY_SYSTEM_TYPES.map((value) => ({
  value,
  label: PROPERTY_SYSTEM_TYPE_LABELS[value],
}));

interface State {
  open: boolean;
  /** leer/undefined beim Anlegen ⇒ Kunde wird im Dialog ausgewählt (z. B. von der Objekte-Übersicht aus) */
  customerId?: string | null;
  propertyId?: string | null;
}

interface Device {
  name: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  year?: string;
}

export function PropertyDialog({
  state,
  onClose,
  onSaved,
}: {
  state: State;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const editing = !!state.propertyId;
  const detail = trpc.property.get.useQuery(
    { id: state.propertyId! },
    { enabled: state.open && editing },
  );
  const needsCustomerPick = !editing && !state.customerId;
  const customers = trpc.customer.list.useQuery(
    { activeOnly: true },
    { enabled: state.open && needsCustomerPick },
  );

  const [f, setF] = useState<Record<string, string>>({});
  const [devices, setDevices] = useState<Device[]>([]);
  // Anlage (Hersteller, Modell, Geräte …) ist optional — Objekt = Adresse/Durchführungsort, die Anlage
  // kann zusätzlich für die Zukunft hinterlegt werden.
  const [withPlant, setWithPlant] = useState(false);
  const [pickedCustomerId, setPickedCustomerId] = useState("");

  useEffect(() => {
    if (state.open) setPickedCustomerId(state.customerId ?? "");
  }, [state.open, state.customerId]);

  useEffect(() => {
    if (!state.open) return;
    if (editing && detail.data) {
      const d = detail.data;
      setF({
        label: d.label,
        systemType: d.systemType,
        addressStreet: d.addressStreet ?? "",
        addressZip: d.addressZip ?? "",
        addressCity: d.addressCity ?? "",
        manufacturer: d.manufacturer ?? "",
        model: d.model ?? "",
        serialNumber: d.serialNumber ?? "",
        commissioningDate: d.commissioningDate
          ? new Date(d.commissioningDate).toISOString().slice(0, 10)
          : "",
        notes: d.notes ?? "",
      });
      const devs = ((d.installedDevices as Device[] | null) ?? []) as Device[];
      setDevices(devs);
      setWithPlant(
        !!(
          d.manufacturer ||
          d.model ||
          d.serialNumber ||
          d.commissioningDate ||
          devs.length ||
          d.systemType !== "sonstige"
        ),
      );
    } else if (!editing) {
      setF({
        label: "",
        systemType: "sonstige",
        addressStreet: "",
        addressZip: "",
        addressCity: "",
        manufacturer: "",
        model: "",
        serialNumber: "",
        commissioningDate: "",
        notes: "",
      });
      setDevices([]);
      setWithPlant(false);
    }
  }, [state.open, editing, detail.data]);

  const done = (id: string, customerId: string) => {
    utils.property.list.invalidate();
    if (state.propertyId)
      utils.property.get.invalidate({ id: state.propertyId });
    utils.customer.get.invalidate({ id: customerId });
    addToast({ type: "success", title: "Objekt gespeichert" });
    onSaved?.(id);
    onClose();
  };
  const onErr = (e: { message: string }) =>
    addToast({ type: "error", title: "Fehler", description: e.message });
  const createM = trpc.property.create.useMutation({
    onSuccess: (p) => done(p.id, p.customerId),
    onError: onErr,
  });
  const updateM = trpc.property.update.useMutation({
    onSuccess: (p) => done(p.id, p.customerId),
    onError: onErr,
  });

  const activeCustomerId = state.customerId || pickedCustomerId;
  const submit = () => {
    const label = f.label?.trim() || f.addressStreet?.trim();
    if (!label || !activeCustomerId) return;
    const payload = {
      customerId: activeCustomerId,
      label,
      systemType: (withPlant ? f.systemType : "sonstige") as never,
      addressStreet: f.addressStreet || null,
      addressZip: f.addressZip || null,
      addressCity: f.addressCity || null,
      manufacturer: withPlant ? f.manufacturer || null : null,
      model: withPlant ? f.model || null : null,
      serialNumber: withPlant ? f.serialNumber || null : null,
      commissioningDate:
        withPlant && f.commissioningDate ? new Date(f.commissioningDate) : null,
      installedDevices: withPlant ? devices.filter((d) => d.name?.trim()) : [],
      notes: f.notes || null,
    };
    if (editing) updateM.mutate({ id: state.propertyId!, ...payload });
    else createM.mutate(payload);
  };

  return (
    <Dialog
      open={state.open}
      onOpenChange={(v) => !v && onClose()}
      title={editing ? "Objekt bearbeiten" : "Neues Objekt"}
      wide
    >
      <div className="grid grid-cols-2 gap-4">
        {needsCustomerPick && (
          <Select
            label="Kunde"
            value={pickedCustomerId}
            onChange={(e) => setPickedCustomerId(e.target.value)}
            options={[
              { value: "", label: "— wählen —" },
              ...(customers.data ?? []).map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]}
          />
        )}
        <Input
          label="Bezeichnung"
          placeholder="z. B. MFH Getreidegasse – Heizraum"
          value={f.label ?? ""}
          onChange={(e) => setF((s) => ({ ...s, label: e.target.value }))}
        />
        <div />
        <div className="col-span-2">
          <p className="label">Adresse / Durchführungsort</p>
          <p className="text-xs text-text-tertiary -mt-1 mb-2">
            Wird in Angebot und Rechnung als Durchführungsort übernommen, wenn
            dort das Hackerl gesetzt ist.
          </p>
        </div>
        <Input
          label="Straße"
          value={f.addressStreet ?? ""}
          onChange={(e) =>
            setF((s) => ({ ...s, addressStreet: e.target.value }))
          }
        />
        <div className="grid grid-cols-[100px_1fr] gap-2">
          <Input
            label="PLZ"
            value={f.addressZip ?? ""}
            onChange={(e) =>
              setF((s) => ({ ...s, addressZip: e.target.value }))
            }
          />
          <Input
            label="Ort"
            value={f.addressCity ?? ""}
            onChange={(e) =>
              setF((s) => ({ ...s, addressCity: e.target.value }))
            }
          />
        </div>
      </div>

      <div className="mt-5 border-t border-border-subtle pt-4">
        <label className="flex items-center gap-2 text-sm font-medium text-text-primary">
          <input
            type="checkbox"
            checked={withPlant}
            onChange={(e) => setWithPlant(e.target.checked)}
          />
          Anlage zu diesem Objekt speichern (optional, für die Zukunft)
        </label>
        {withPlant && (
          <div className="mt-3">
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Anlagentyp"
                value={f.systemType ?? "heizung"}
                onChange={(e) =>
                  setF((s) => ({ ...s, systemType: e.target.value }))
                }
                options={SYSTEM_TYPES}
              />
              <div />
              <Input
                label="Hersteller"
                value={f.manufacturer ?? ""}
                onChange={(e) =>
                  setF((s) => ({ ...s, manufacturer: e.target.value }))
                }
              />
              <Input
                label="Modell"
                value={f.model ?? ""}
                onChange={(e) => setF((s) => ({ ...s, model: e.target.value }))}
              />
              <Input
                label="Seriennummer"
                value={f.serialNumber ?? ""}
                onChange={(e) =>
                  setF((s) => ({ ...s, serialNumber: e.target.value }))
                }
              />
              <Input
                label="Inbetriebnahme"
                type="date"
                value={f.commissioningDate ?? ""}
                onChange={(e) =>
                  setF((s) => ({ ...s, commissioningDate: e.target.value }))
                }
              />
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="label mb-0">Verbaute Geräte</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDevices([...devices, { name: "" }])}
                >
                  <Plus className="w-3.5 h-3.5" /> Gerät
                </Button>
              </div>
              {devices.map((d, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_1fr_1fr_80px_28px] gap-2 mb-1.5"
                >
                  <input
                    className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                    placeholder="Name"
                    value={d.name}
                    onChange={(e) =>
                      setDevices(
                        devices.map((x, j) =>
                          j === i ? { ...x, name: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  <input
                    className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                    placeholder="Hersteller"
                    value={d.manufacturer ?? ""}
                    onChange={(e) =>
                      setDevices(
                        devices.map((x, j) =>
                          j === i ? { ...x, manufacturer: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  <input
                    className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                    placeholder="Seriennr."
                    value={d.serial ?? ""}
                    onChange={(e) =>
                      setDevices(
                        devices.map((x, j) =>
                          j === i ? { ...x, serial: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  <input
                    className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm"
                    placeholder="Jahr"
                    value={d.year ?? ""}
                    onChange={(e) =>
                      setDevices(
                        devices.map((x, j) =>
                          j === i ? { ...x, year: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  <button
                    className="text-text-tertiary hover:text-danger"
                    onClick={() =>
                      setDevices(devices.filter((_, j) => j !== i))
                    }
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3">
        <Textarea
          label="Notizen"
          rows={2}
          value={f.notes ?? ""}
          onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))}
        />
      </div>

      {editing && (
        <div className="mt-5 border-t border-border-subtle pt-4">
          <PropertyJournalSection propertyId={state.propertyId!} />
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          loading={createM.isPending || updateM.isPending}
          disabled={
            !(f.label?.trim() || f.addressStreet?.trim()) || !activeCustomerId
          }
        >
          Speichern
        </Button>
      </div>
    </Dialog>
  );
}
